import { ReactNode } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";

import { ContentBlock } from "@/components/wpl/content-block/src";
import { Image } from "@/components/wpl/image/src";
import { DEFAULT_VALUES, useLocalizedHref } from "@/libraries/web-framework";
import { Row } from "@/libraries/wpl/foundations/src";

import { getScailingSectionJiraFreeImage } from "../../../localized-images";

const backgroundStyles = css({
    backgroundColor: "#091e42",
    backgroundRepeat: "no-repeat",
    color: "#ffffff",
    padding: "32px 0",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    a: {
        textDecoration: "underline"
    },

    [media.only.sm]: {
        backgroundImage:
            "linear-gradient(180deg, #b3d4ff 0%, #b3d4ff00 84.73%)",
        backgroundPosition: "center",
        backgroundSize: "cover"
    },

    [media.only.md]: {
        backgroundImage: `url(https://dam-cdn.atl.orangelogic.com/AssetLink/05485fnhmx16cj7ux4g1yw76h2rukc80.svg)`,
        backgroundPosition: "center top 170px",
        backgroundSize: "1300px"
    },

    [media.above.lg]: {
        backgroundImage: `url(https://dam-cdn.atl.orangelogic.com/AssetLink/05485fnhmx16cj7ux4g1yw76h2rukc80.svg)`,
        backgroundPosition: "center top 50px",
        backgroundSize: "1480px"
    }
});

const ScalingSection = () => {
    const intl = useIntl();

    return (
        <section css={backgroundStyles}>
            <Row>
                <div
                    css={css({
                        [media.only.sm]: {
                            display: "flex",
                            gap: "32px"
                        }
                    })}
                >
                    <div
                        css={css({
                            padding: "16px 0",
                            width: "41.66%",
                            maxWidth: "490px",
                            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                            h2: {
                                fontSize: "2.25rem",
                                fontWeight: "normal",
                                lineHeight: "1.333",
                                marginBlockEnd: "24px"
                            },
                            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                            p: {
                                fontSize: "1rem",
                                lineHeight: "1.5"
                            },

                            [media.only.md]: {
                                width: "33%"
                            },

                            [media.below.sm]: {
                                width: "100%",
                                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                                h2: {
                                    fontSize: "1.5rem"
                                }
                            }
                        })}
                    >
                        <ContentBlock
                            headerType="h2"
                            headerText={intl.formatMessage({
                                id: "JiraSoftwarePageTemplate.scalingSection.headerText",
                                defaultMessage: "Built for teams of 1 to 35,000"
                            })}
                            bodyText={intl.formatMessage({
                                id: "JiraSoftwarePageTemplate.scalingSection.bodyText",
                                defaultMessage:
                                    "A growing team doesn’t need to mean growing pains. With best-of-breed features, security, privacy, and the right tool for every step of your journey - Jira allows you to scale without friction - regardless of your company size."
                            })}
                        />
                    </div>
                    <div
                        css={css({
                            display: "none",
                            backgroundImage: `linear-gradient(
                        to bottom,
                        #0065ff,
                        #2684ff
                    )`,
                            backgroundPosition: "left 48px center",
                            backgroundRepeat: "no-repeat",
                            backgroundSize: "5px 67%",

                            [media.below.md]: {
                                display: "flex"
                            }
                        })}
                    >
                        <div
                            css={css({
                                display: "flex",
                                flexDirection: "column",
                                gap: "42px",
                                padding: "16px 0"
                            })}
                        >
                            <ScalingStepsForSmallScreen
                                imageSrc={getScailingSectionJiraFreeImage(
                                    intl.locale
                                )}
                                imageAltText={intl.formatMessage({
                                    id: "JiraSoftwarePageTemplate.scalingSection.small.jiraFree",
                                    defaultMessage: "Jira free"
                                })}
                                bodyText={
                                    <FormattedMessage
                                        id="JiraSoftwarePageTemplate.scalingSection.small.jiraSoftwareFree"
                                        defaultMessage="Jira free forever for teams up to 10"
                                        values={DEFAULT_VALUES}
                                    />
                                }
                            />
                            <ScalingStepsForSmallScreen
                                imageSrc="https://dam-cdn.atl.orangelogic.com/AssetLink/j35w660pt742q2m6rnk083y2610rnw71.webp"
                                imageAltText={intl.formatMessage({
                                    id: "JiraSoftwarePageTemplate.scalingSection.small.jiraSoftware",
                                    defaultMessage: "Jira Software"
                                })}
                                bodyText={
                                    <FormattedMessage
                                        id="JiraSoftwarePageTemplate.scalingSection.small.jiraSoftwareStandard"
                                        defaultMessage="Jira Standard for growing teams"
                                        values={DEFAULT_VALUES}
                                    />
                                }
                            />
                            <ScalingStepsForSmallScreen
                                imageSrc="https://dam-cdn.atl.orangelogic.com/AssetLink/55gl86x3tg73mwa05i2qifsd51400818.webp"
                                imageAltText={intl.formatMessage({
                                    id: "JiraSoftwarePageTemplate.scalingSection.small.jiraSoftwarePremium",
                                    defaultMessage: "Jira Premium"
                                })}
                                bodyText={
                                    <FormattedMessage
                                        id="JiraSoftwarePageTemplate.scalingSection.small.jiraSoftwarePremiumScale"
                                        defaultMessage="Scale across teams with <a>Jira Premium</a>"
                                        values={{
                                            a: (str: ReactNode) => (
                                                <a
                                                    // eslint-disable-next-line react-hooks/rules-of-hooks
                                                    href={useLocalizedHref(
                                                        "/software/jira/premium"
                                                    )}
                                                    data-event-component="linkButton"
                                                    data-event-container="textBlock"
                                                    data-event="clicked"
                                                    data-label="Jira Premium"
                                                    data-product-key="jiraSoftware"
                                                >
                                                    {str}
                                                </a>
                                            ),
                                            ...DEFAULT_VALUES
                                        }}
                                    />
                                }
                            />
                            <ScalingStepsForSmallScreen
                                imageSrc="https://dam-cdn.atl.orangelogic.com/AssetLink/6p36547re745xu475rp6214nr53vj3q0.webp"
                                imageAltText={intl.formatMessage({
                                    id: "JiraSoftwarePageTemplate.scalingSection.small.jiraSoftwareEnterprise",
                                    defaultMessage: "Jira Enterprise"
                                })}
                                bodyText={
                                    <FormattedMessage
                                        id="JiraSoftwarePageTemplate.scalingSection.small.jiraSoftwareEnterpriseConnect"
                                        defaultMessage="Connect and accelerate your entire <a>enterprise</a>"
                                        values={{
                                            a: (str: ReactNode) => (
                                                <a
                                                    // eslint-disable-next-line react-hooks/rules-of-hooks
                                                    href={useLocalizedHref(
                                                        "/software/jira/enterprise"
                                                    )}
                                                    data-event-component="linkButton"
                                                    data-event-container="textBlock"
                                                    data-event="clicked"
                                                    data-label="enterprise"
                                                    data-product-key="jiraSoftware"
                                                >
                                                    {str}
                                                </a>
                                            ),
                                            ...DEFAULT_VALUES
                                        }}
                                    />
                                }
                            />
                            <ScalingStepsForSmallScreen
                                imageSrc="https://dam-cdn.atl.orangelogic.com/AssetLink/s0f7hv6783r3srwgwj2ko2e1rk8808at.webp"
                                imageAltText={intl.formatMessage({
                                    id: "JiraSoftwarePageTemplate.scalingSection.small.jiraAlign",
                                    defaultMessage: "Jira Align"
                                })}
                                bodyText={
                                    <FormattedMessage
                                        id="JiraSoftwarePageTemplate.scalingSection.small.jiraAlignEnterprise"
                                        defaultMessage="Enterprise agility with Jira Align"
                                        values={DEFAULT_VALUES}
                                    />
                                }
                            />
                        </div>
                    </div>
                    <div
                        css={css({
                            padding: "16px 0",
                            marginBlockStart: "-80px",
                            marginBlockEnd: "20px",

                            [media.below.md]: {
                                display: "none"
                            }
                        })}
                    >
                        <div
                            css={css({
                                display: "flex",
                                justifyContent: "center",
                                backgroundImage: `linear-gradient(
                            90deg,
                            #2684ff 5.1%,
                            #79f2c0 100%
                        )`,
                                backgroundPosition: "center 244px",
                                backgroundRepeat: "no-repeat",
                                backgroundSize: "100% 5px",
                                margin: "auto",
                                // rules below are added to expand div beyond the grid
                                position: "relative",
                                insetInlineStart: "50%",
                                transform: "translateX(-50%)",
                                width: "125%",
                                // calc for 1440px with 16px padding
                                maxWidth: "calc(100vw - 32px)"
                            })}
                        >
                            <ScalingStepsForLargeScreen
                                imageSrc={getScailingSectionJiraFreeImage(
                                    intl.locale
                                )}
                                imageAltText={intl.formatMessage({
                                    id: "JiraSoftwarePageTemplate.scalingSection.large.jiraFree",
                                    defaultMessage: "Jira free"
                                })}
                                cssTop="80px"
                                bodyText={
                                    <FormattedMessage
                                        id="JiraSoftwarePageTemplate.scalingSection.large.jiraSoftwareFree"
                                        defaultMessage="Jira free forever for teams up to 10"
                                        values={DEFAULT_VALUES}
                                    />
                                }
                            />
                            <ScalingStepsForLargeScreen
                                imageSrc="https://dam-cdn.atl.orangelogic.com/AssetLink/j35w660pt742q2m6rnk083y2610rnw71.webp"
                                imageAltText={intl.formatMessage({
                                    id: "JiraSoftwarePageTemplate.scalingSection.large.jiraSoftware",
                                    defaultMessage: "Jira"
                                })}
                                cssTop="60px"
                                bodyText={
                                    <FormattedMessage
                                        id="JiraSoftwarePageTemplate.scalingSection.large.jiraSoftwareStandard"
                                        defaultMessage="Jira Standard for growing teams"
                                        values={DEFAULT_VALUES}
                                    />
                                }
                            />
                            <ScalingStepsForLargeScreen
                                imageSrc="https://dam-cdn.atl.orangelogic.com/AssetLink/55gl86x3tg73mwa05i2qifsd51400818.webp"
                                imageAltText={intl.formatMessage({
                                    id: "JiraSoftwarePageTemplate.scalingSection.large.jiraSoftwarePremium",
                                    defaultMessage: "Jira Premium"
                                })}
                                cssTop="40px"
                                bodyText={
                                    <FormattedMessage
                                        id="JiraSoftwarePageTemplate.scalingSection.large.jiraSoftwarePremiumScale"
                                        defaultMessage="Scale across teams with <a>Jira Premium</a>"
                                        values={{
                                            a: (str: ReactNode) => (
                                                <a
                                                    // eslint-disable-next-line react-hooks/rules-of-hooks
                                                    href={useLocalizedHref(
                                                        "/software/jira/premium"
                                                    )}
                                                    data-event-component="linkButton"
                                                    data-event-container="textBlock"
                                                    data-event="clicked"
                                                    data-label="Jira Premium"
                                                    data-product-key="jiraSoftware"
                                                >
                                                    {str}
                                                </a>
                                            ),
                                            ...DEFAULT_VALUES
                                        }}
                                    />
                                }
                            />
                            <ScalingStepsForLargeScreen
                                imageSrc="https://dam-cdn.atl.orangelogic.com/AssetLink/6p36547re745xu475rp6214nr53vj3q0.webp"
                                imageAltText={intl.formatMessage({
                                    id: "JiraSoftwarePageTemplate.scalingSection.large.jiraSoftwareEnterprise",
                                    defaultMessage: "Jira Enterprise"
                                })}
                                cssTop="20px"
                                // disabling eslint because the bodyText prop is a ReactNode
                                bodyText={
                                    <FormattedMessage
                                        id="JiraSoftwarePageTemplate.scalingSection.large.jiraSoftwareEnterpriseConnect"
                                        defaultMessage="Connect and accelerate your entire <a>enterprise</a>"
                                        values={{
                                            a: (str: ReactNode) => (
                                                <a
                                                    // eslint-disable-next-line react-hooks/rules-of-hooks
                                                    href={useLocalizedHref(
                                                        "/software/jira/enterprise"
                                                    )}
                                                    data-event-component="linkButton"
                                                    data-event-container="textBlock"
                                                    data-event="clicked"
                                                    data-label="enterprise"
                                                    data-product-key="jiraSoftware"
                                                >
                                                    {str}
                                                </a>
                                            ),
                                            ...DEFAULT_VALUES
                                        }}
                                    />
                                }
                            />
                            <ScalingStepsForLargeScreen
                                imageSrc="https://dam-cdn.atl.orangelogic.com/AssetLink/s0f7hv6783r3srwgwj2ko2e1rk8808at.webp"
                                imageAltText={intl.formatMessage({
                                    id: "JiraSoftwarePageTemplate.scalingSection.large.jiraAlign",
                                    defaultMessage: "Jira Align"
                                })}
                                cssTop="0"
                                bodyText={
                                    <FormattedMessage
                                        id="JiraSoftwarePageTemplate.scalingSection.large.jiraAlignEnterprise"
                                        defaultMessage="Enterprise agility with Jira Align"
                                        values={DEFAULT_VALUES}
                                    />
                                }
                            />
                        </div>
                    </div>
                </div>
            </Row>
        </section>
    );
};

interface IconProps {
    imageSrc: string;
    imageAltText: string;
    cssTop?: string;
    bodyText: string | ReactNode;
}

const ScalingStepsForSmallScreen = (props: IconProps) => {
    return (
        <div
            css={css({
                display: "flex"
            })}
        >
            <div
                css={css({
                    display: "flex",
                    alignItems: "center",
                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                    img: {
                        height: "100px",
                        minWidth: "100px",
                        width: "100px",
                        zIndex: "2"
                    }
                })}
            >
                <Image src={props.imageSrc} alt={props.imageAltText} />
            </div>
            <div
                css={css({
                    alignItems: "center",
                    display: "flex",
                    paddingInlineStart: "32px",
                    width: "50%",

                    [media.below.md]: {
                        width: "auto"
                    }
                })}
            >
                <p
                    css={css({
                        fontSize: "1rem",
                        margin: "0",
                        lineHeight: "1.5"
                    })}
                >
                    {props.bodyText}
                </p>
            </div>
        </div>
    );
};

const ScalingStepsForLargeScreen = (props: IconProps) => {
    return (
        <div
            css={css({
                alignItems: "center",
                display: "flex",
                flexDirection: "column",
                padding: "16px",
                width: "16.66%"
            })}
        >
            <div>
                <div
                    css={css({
                        "display": "flex",
                        "justifyContent": "center",
                        "minHeight": "220px",
                        "position": "relative",
                        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                        "img": {
                            marginBlockEnd: "16px",
                            position: "relative",
                            insetBlockStart: `${props.cssTop}`,
                            width: "100px",
                            height: "100px",
                            zIndex: "2"
                        },

                        ":after": {
                            content: "",
                            position: "absolute",
                            height: "180px",
                            insetBlockStart: "95px",
                            border: "2px solid #2684ff",
                            backgroundSize: "4px 8px",
                            backgroundRepeat: "repeat-y",
                            maxHeight: "-webkit-fill-available",
                            zIndex: "1"
                        }
                    })}
                >
                    <Image src={props.imageSrc} alt={props.imageAltText} />
                </div>
                <div
                    css={css({
                        backgroundColor: "#2684ff",
                        borderRadius: "50%",
                        height: "20px",
                        margin: "0 auto 30px",
                        width: "20px"
                    })}
                />
                <div>
                    <p
                        css={css({
                            fontSize: "1rem",
                            margin: "0",
                            textAlign: "center",
                            lineHeight: "1.5"
                        })}
                    >
                        {props.bodyText}
                    </p>
                </div>
            </div>
        </div>
    );
};

ScalingSection.displayName = "ScalingSection";

export default ScalingSection;
